/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError
} from "@azure/msal-browser";

let msalInstance = null;


// =========================
// CONFIGURA ESTO
// =========================
const CLIENT_ID = "76a50a38-4f71-401f-b97a-8daf2ac2178f";
const AUTHORITY = "https://login.microsoftonline.com/common";

// Cambia esto por tu tenant/sharepoint real
const SHAREPOINT_HOSTNAME = "disanltdacl.sharepoint.com";
const SHAREPOINT_SITE_PATH = "/sites/TaskPaneTest";

async function initMsal() {
  if (!msalInstance) {
    const msalConfig = {
      auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY
      },
      cache: {
        cacheLocation: "localStorage"
      }
    };

    msalInstance = await createNestablePublicClientApplication(msalConfig);
  }
}

async function getLoginHint() {
  try {
    if (typeof Office !== "undefined" && Office.context) {
      const authContext = await Office.auth.getAuthContext();
      if (authContext?.userPrincipalName) {
        return authContext.userPrincipalName;
      }
    }
  } catch (error) {
    console.warn("No se pudo obtener login hint:", error);
  }

  return null;
}

async function acquireAccessToken(scopes) {
  await initMsal();

  const request = {
    scopes,
    loginHint: await getLoginHint()
  };

  let authResult = null;

  try {
    authResult = await msalInstance.ssoSilent(request);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      authResult = await msalInstance.acquireTokenPopup(request);
    } else {
      console.error("Error al obtener token de acceso:", error);
      throw error;
    }
  }

  if (!authResult) {
    throw new Error("No se pudo obtener access token");
  }

  return authResult.accessToken;
}

async function probarUsuario() {
  const output = document.getElementById("output");

  try {
    const accessToken = await acquireAccessToken(["User.Read"]);

    const response = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    const data = await response.json();
    output.textContent = `Hola ${data.displayName} (${data.userPrincipalName})`;
  } catch (error) {
    console.error(error);
    output.textContent = `Error: ${error.message}`;
  }
}

// =========================
// HELPERS GRAPH
// =========================
async function graphGet(token, url) {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Graph error ${response.status}: ${text}`);
  }

  return response.json();
}

// =========================
// SHAREPOINT
// =========================
async function getSite(token) {
  const url = `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_HOSTNAME}:${SHAREPOINT_SITE_PATH}`;
  return graphGet(token, url);
}

async function getDrives(token, siteId) {
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives`;
  return graphGet(token, url);
}

async function getFilesFromDriveRoot(token, driveId) {
  const url =
    `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children` +
    `?$select=id,name,webUrl,lastModifiedDateTime,size,file,folder`;
  return graphGet(token, url);
}

// =========================
// UI
// =========================
function setStatus(message, isError = false) {
  const status = document.getElementById("status");
  if (!status) return;

  status.textContent = message;
  status.style.color = isError ? "crimson" : "inherit";
}

function clearList() {
  const list = document.getElementById("output");
  if (list) {
    list.innerHTML = "";
  }
}

function renderFiles(items) {
  const container = document.getElementById("output");
  if (!container) return;

  container.innerHTML = "";

  if (!items || items.length === 0) {
    container.innerHTML = "<div>No se encontraron archivos.</div>";
    return;
  }

  for (const item of items) {
    const row = document.createElement("div");
    row.style.padding = "8px";
    row.style.borderBottom = "1px solid #ddd";

    const link = document.createElement("a");

    const openUrl = item.webUrl + "?web=0";
    link.href = openUrl || item.webUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = item.folder ? `📁 ${item.name}` : `📄 ${item.name}`;
    link.style.fontWeight = "600";
    link.style.textDecoration = "none";

    const meta = document.createElement("div");
    meta.style.fontSize = "12px";
    meta.style.marginTop = "4px";

    const type = item.folder ? "Carpeta" : "Archivo";
    const modified = item.lastModifiedDateTime
      ? new Date(item.lastModifiedDateTime).toLocaleString()
      : "Sin fecha";
    const size =
      typeof item.size === "number" ? ` · ${item.size.toLocaleString()} bytes` : "";

    meta.textContent = `${type} · ${modified}${size}`;

    row.appendChild(link);
    row.appendChild(meta);
    container.appendChild(row);
  }
}

// =========================
// FLUJO COMPLETO
// =========================
async function listarDocumentosSharePoint() {
  clearList();
  setStatus("Conectando con Microsoft 365...");

  try {
    const token = await acquireAccessToken([
      "User.Read",
      "Sites.Read.All",
      "Files.Read.All",
    ]);

    setStatus("Resolviendo sitio de SharePoint...");

    const site = await getSite(token);
    if (!site?.id) {
      throw new Error("No se pudo resolver el sitio de SharePoint.");
    }

    setStatus("Buscando bibliotecas...");

    const drives = await getDrives(token, site.id);
    if (!drives?.value?.length) {
      throw new Error("No se encontraron bibliotecas en el sitio.");
    }

    const drive =
      drives.value.find((d) => d.name === "Documents") ||
      drives.value.find((d) => d.name === "Documentos") ||
      drives.value[0];

    if (!drive?.id) {
      throw new Error("No se encontró una biblioteca válida.");
    }

    setStatus(`Listando documentos de la biblioteca: ${drive.name}...`);

    const files = await getFilesFromDriveRoot(token, drive.id);
    renderFiles(files.value || []);

    setStatus(`Listo. Biblioteca usada: ${drive.name}`);
  } catch (error) {
    console.error(error);
    setStatus(`Error: ${error.message}`, true);
  }
}


/* global console, document, Excel, Office */
Office.onReady(async (info) => {
  const supported = Office.context?.requirements?.isSetSupported?.("NestedAppAuth", "1.1") ?? false;

  if (!supported) {
    setStatus("Este host no soporta Nested App Authentication 1.1", true);
    return
  }

  await initMsal();

  const btnLoad = document.getElementById("btn-load");
  if (btnLoad) {
    btnLoad.onclick = listarDocumentosSharePoint;
  }

  setStatus("Listo para cargar documentos.");


  const btnLogin = document.getElementById("btn-login");
  if (btnLogin) {
    btnLogin.onclick = probarUsuario;
  }

  if (info.host === Office.HostType.Excel) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";
    // document.getElementById("run").onclick = run;
  }
});

export async function run() {
  try {
    await Excel.run(async (context) => {
      /**
       * Insert your Excel code here
       */
      const range = context.workbook.getSelectedRange();

      // Read the range address
      range.load("address");

      // Update the fill color
      range.format.fill.color = "yellow";

      await context.sync();
      console.log(`The range address was ${range.address}.`);
    });
  } catch (error) {
    console.error(error);
  }
}
